import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Sparkles, ShieldCheck, Check, ArrowRight, Crown, Calendar,
  CreditCard, Loader2, Gift, RefreshCw, XCircle, RotateCw,
} from "lucide-react";

const PLAN_FEATURES_STANDARD = [
  "Dashboard + Launch Checklist",
  "AI Social Post Generator + Saved Posts",
  "Speech Writer",
  "Graphics (Beta)",
  "Campaign Advisor (AI chat)",
  "Campaign Calendar + Scheduler",
];
const PLAN_FEATURES_PRO_EXTRA = [
  "Everything in Standard",
  "Voter List CRM (upload, tag, AI filter, export)",
  "Finances suite — fundraising & donor tracking",
  "Budget + expense tracking, account transactions, reimbursements",
  "Report builder with CSV export for filing periods",
];

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export default function Billing() {
  const { user, reload: reloadUser } = useAuth();  // eslint-disable-line no-unused-vars
  const billing = useBilling();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [interval, setInterval] = useState("yearly");  // default to annual (10% off) — nudges the better long-term price

  const isSubscribed = billing.status === "pro" || billing.status === "standard";
  const canceling = isSubscribed && billing.cancel_at_period_end;
  const yearlyConfigured = !!(billing.plans?.standard?.yearly?.configured && billing.plans?.pro?.yearly?.configured);
  const effectiveInterval = yearlyConfigured ? interval : "monthly";

  // Post-checkout reconcile — polls checkout/status until Stripe reports
  // paid, then reloads the entitlement so the trial badge disappears.
  useEffect(() => {
    const success = searchParams.get("success");
    const sid = searchParams.get("session_id");
    const canceled = searchParams.get("canceled");
    const cameFromActivation = searchParams.get("activation") === "1";
    if (canceled) {
      toast.message("Checkout canceled. Your trial is still active until it expires.");
      const next = new URLSearchParams(searchParams);
      next.delete("canceled");
      setSearchParams(next, { replace: true });
    }
    if (success && sid) {
      setVerifying(true);
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/billing/checkout/status/${sid}`);
          if (data.payment_status === "paid" || data.payment_status === "no_payment_required" || data.entitlement?.can_paid) {
            toast.success("Subscription active. Welcome aboard!");
            // Belt + braces: on some Stripe API versions the checkout session
            // returns before the subscription object is fully populated. A
            // reconcile against Stripe's Subscription API guarantees the
            // period_end lands even if the session-level payload was thin.
            try { await api.post("/billing/reconcile"); } catch { /* non-fatal */ }
            await billing.reload();
            // Refresh the auth user so ActivationGate sees the fresh
            // activation_status=active — otherwise a card-required signup
            // completing checkout would still be walled off.
            try { await reloadUser(); } catch { /* non-fatal */ }
            // Clear the activation-redirect one-shot so subsequent
            // sessions never accidentally re-loop through Stripe.
            try { sessionStorage.removeItem("activation_redirect_started"); } catch { /* noop */ }
            const next = new URLSearchParams(searchParams);
            next.delete("success");
            next.delete("session_id");
            next.delete("activation");
            setSearchParams(next, { replace: true });
            setVerifying(false);
            if (cameFromActivation) {
              // Register → card → here. Send the user straight to race-info
              // intake so they're not dead-ended on the Billing page.
              navigate("/intake");
            }
            return;
          }
          if (data.status === "expired") {
            toast.error("That checkout session expired — please try again.");
            setVerifying(false);
            return;
          }
        } catch (err) {
          console.debug("checkout poll error", err);
        }
        if (attempts < 8) setTimeout(poll, 2000);
        else {
          toast.message("Still confirming payment with Stripe — click Refresh below.");
          setVerifying(false);
        }
      };
      poll();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startCheckout = async (plan) => {
    if (billing.is_owner) { toast.message("Owner accounts already have full access."); return; }
    if (billing.is_comp)  { toast.message("Your account is on a permanent comp — no checkout needed."); return; }
    setBusy(`${plan}-${effectiveInterval}`);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan,
        interval: effectiveInterval,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else { toast.error("Couldn't open Stripe checkout."); setBusy(""); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy("");
    }
  };

  const cancelSubscription = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success(`Canceled. You keep access until ${fmtDate(billing.current_period_end) || "the end of your current period"}.`);
      await billing.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(""); }
  };

  const resumeSubscription = async () => {
    setBusy("resume");
    try {
      await api.post("/billing/resume");
      toast.success("Subscription resumed — it will renew as normal.");
      await billing.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(""); }
  };

  const reconcile = async () => {
    setBusy("reconcile");
    try {
      const { data } = await api.post("/billing/reconcile");
      await billing.reload();
      if (data.reconciled) toast.success("Refreshed from Stripe.");
      else toast.message("Nothing to reconcile — Stripe agrees with what we show.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(""); }
  };

  const redeemCode = async () => {
    const v = code.trim();
    if (!v) return;
    setBusy("code");
    try {
      const { data } = await api.post("/billing/redeem-code", { code: v });
      toast.success(data.already_redeemed ? "Code already active on this account." : "Code accepted! Full Pro access unlocked.");
      setCode("");
      await billing.reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "That code isn't valid.");
    } finally { setBusy(""); }
  };

  const tierBadge = (() => {
    if (billing.is_owner) return { label: "Owner — full access", icon: Crown, color: "bg-amber-100 text-amber-900" };
    if (billing.is_comp)  return { label: `Comp (${billing.comp_code || "active"})`, icon: Gift, color: "bg-emerald-100 text-emerald-900" };
    if (billing.status === "trial") return { label: `Trial — ${billing.trial_days_left} day${billing.trial_days_left === 1 ? "" : "s"} left`, icon: Sparkles, color: "bg-amber-100 text-amber-900" };
    if (billing.status === "pro") return { label: canceling ? "Pro — will not renew" : "Pro — active", icon: ShieldCheck, color: canceling ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900" };
    if (billing.status === "standard") return { label: canceling ? "Standard — will not renew" : "Standard — active", icon: ShieldCheck, color: canceling ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900" };
    return { label: "No active plan", icon: Calendar, color: "bg-slate-200 text-slate-700" };
  })();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-6">
        <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Billing</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1" data-testid="billing-title">Your plan</h1>
        <p className="text-slate-600 mt-2">Manage your Electable subscription, upgrade tiers, or switch to annual for 10% off.</p>
      </div>

      {/* Current status */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4" data-testid="billing-current-card">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${tierBadge.color}`}>
            <tierBadge.icon className="h-5 w-5" />
          </span>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Current status</div>
            <div className="font-display text-lg font-bold text-flag-navy" data-testid="billing-status-label">
              {tierBadge.label}
            </div>
            {billing.current_period_end && isSubscribed && (
              <div className="text-xs text-slate-500 mt-0.5" data-testid="billing-period-end">
                {canceling
                  ? <>Active until <strong>{fmtDate(billing.current_period_end)}</strong> — will not renew.</>
                  : <>Renews {fmtDate(billing.current_period_end)}
                     {billing.interval === "yearly" ? " · billed annually" : " · billed monthly"}</>
                }
              </div>
            )}
            {billing.status === "trial" && billing.trial_ends_at && (
              <div className="text-xs text-slate-500 mt-0.5">
                Trial ends {fmtDate(billing.trial_ends_at)} — no card on file, no charge.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isSubscribed && !billing.is_owner && !billing.is_comp && (
            <Button
              onClick={reconcile}
              disabled={busy === "reconcile"}
              variant="ghost"
              data-testid="billing-refresh-btn"
              className="h-9 text-slate-600 hover:text-flag-navy font-semibold"
              title="Ask Stripe for the latest — use if a payment succeeded but the status looks stale"
            >
              {busy === "reconcile" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh
            </Button>
          )}
          {isSubscribed && !canceling && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="billing-cancel-btn"
                  className="h-10 text-flag-red hover:bg-red-50 font-semibold"
                >
                  <XCircle className="h-4 w-4 mr-1.5" /> Cancel subscription
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="billing-cancel-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You'll keep access until <strong>{fmtDate(billing.current_period_end) || "the end of the current period"}</strong>, then your account moves to inactive. No proration, no early cutoff. You can resume anytime before then.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="billing-cancel-nvm">Never mind</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={cancelSubscription}
                    disabled={busy === "cancel"}
                    data-testid="billing-cancel-confirm-yes"
                    className="bg-flag-red hover:bg-flag-red-hover"
                  >
                    {busy === "cancel" ? "Canceling…" : "Yes, cancel at period end"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {isSubscribed && canceling && (
            <Button
              onClick={resumeSubscription}
              disabled={busy === "resume"}
              data-testid="billing-resume-btn"
              className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            >
              {busy === "resume" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCw className="h-4 w-4 mr-1.5" />}
              Resume subscription
            </Button>
          )}
        </div>
      </div>

      {verifying && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="billing-verifying">
          <Loader2 className="h-4 w-4 animate-spin text-amber-700" />
          <span className="text-sm text-amber-900">Confirming your payment with Stripe…</span>
        </div>
      )}

      {/* Plans + interval toggle. Shown for trial, expired, and active
          subscribers alike so an active subscriber can still upgrade
          (Standard → Pro) or switch monthly ↔ annual. */}
      {!billing.is_owner && !billing.is_comp && (
        <>
          {yearlyConfigured && (
            <div className="mt-8 flex items-center justify-center" data-testid="billing-interval-toggle">
              <div className="inline-flex bg-slate-100 rounded-lg p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setInterval("monthly")}
                  data-testid="billing-interval-monthly"
                  className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${interval === "monthly" ? "bg-white text-flag-navy shadow" : "text-slate-600 hover:text-flag-navy"}`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setInterval("yearly")}
                  data-testid="billing-interval-yearly"
                  className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors inline-flex items-center gap-1.5 ${interval === "yearly" ? "bg-white text-flag-navy shadow" : "text-slate-600 hover:text-flag-navy"}`}
                >
                  Annual
                  <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">Save 10%</span>
                </button>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5 mt-6">
            <PlanCard
              plan="standard"
              name="Standard"
              features={PLAN_FEATURES_STANDARD}
              current={billing.status === "standard" && billing.interval === effectiveInterval}
              onSubscribe={() => startCheckout("standard")}
              busy={busy === `standard-${effectiveInterval}`}
              disabled={!billing.stripe_configured || (effectiveInterval === "yearly" && !billing.plans?.standard?.yearly?.configured)}
              plans={billing.plans?.standard}
              interval={effectiveInterval}
              cta={billing.status === "pro" ? "Downgrade" : (isSubscribed ? "Switch to Standard" : "Subscribe")}
            />
            <PlanCard
              plan="pro"
              name="Pro"
              highlight
              features={PLAN_FEATURES_PRO_EXTRA}
              current={billing.status === "pro" && billing.interval === effectiveInterval}
              onSubscribe={() => startCheckout("pro")}
              busy={busy === `pro-${effectiveInterval}`}
              disabled={!billing.stripe_configured || (effectiveInterval === "yearly" && !billing.plans?.pro?.yearly?.configured)}
              plans={billing.plans?.pro}
              interval={effectiveInterval}
              cta={billing.status === "standard" ? "Upgrade to Pro" : (isSubscribed ? "Switch to Pro" : "Subscribe")}
            />
          </div>

          {effectiveInterval === "yearly" && !yearlyConfigured && (
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600" data-testid="billing-yearly-not-configured">
              Annual pricing isn't fully configured yet. Ask the site owner to add <code>STRIPE_PRICE_STANDARD_YEARLY</code> and <code>STRIPE_PRICE_PRO_YEARLY</code> to the backend .env.
            </div>
          )}
        </>
      )}

      {!billing.stripe_configured && !billing.is_owner && !billing.is_comp && (
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600" data-testid="billing-stripe-not-configured">
          <strong>Stripe isn't set up yet.</strong> The site owner needs to paste the Stripe keys + price IDs into the backend <code>.env</code> before checkout can run. You can still redeem an access code below.
        </div>
      )}

      {/* Access code */}
      {!billing.is_owner && !billing.is_comp && (
        <div className="mt-8 bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6" data-testid="billing-code-card">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-flag-red" />
            <h2 className="font-display text-lg font-bold text-flag-navy">Have an access code?</h2>
          </div>
          <p className="mt-1 text-sm text-slate-600">Comp codes unlock the full app with no card and no subscription.</p>
          <div className="mt-4 flex flex-wrap items-center gap-2 max-w-md">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter code"
              data-testid="billing-code-input"
              className="flex-1 min-w-[180px] h-10 uppercase tracking-wider"
              onKeyDown={(e) => { if (e.key === "Enter") redeemCode(); }}
            />
            <Button
              onClick={redeemCode}
              disabled={busy === "code" || !code.trim()}
              data-testid="billing-code-submit"
              className="h-10 bg-flag-navy hover:bg-flag-navy-2 text-white font-bold"
            >
              {busy === "code" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
            </Button>
          </div>
        </div>
      )}

      {(billing.is_owner || billing.is_comp) && (
        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-5 sm:p-6" data-testid="billing-comp-card">
          <h2 className="font-display text-lg font-bold text-emerald-900 flex items-center gap-2">
            <Crown className="h-5 w-5" />
            {billing.is_owner ? "Owner account" : "Permanent comp"}
          </h2>
          <p className="mt-1 text-sm text-emerald-900/80">
            You have full Pro access. No card on file, no Stripe involvement, no risk of being charged.
          </p>
        </div>
      )}
    </div>
  );
}

function PlanCard({ name, features, current, onSubscribe, busy, disabled, highlight, plan, plans, interval, cta }) {
  const monthly = plans?.monthly?.price_usd ?? 0;
  const yearly = plans?.yearly?.price_usd ?? 0;
  const showYearly = interval === "yearly";
  const monthlyEquiv = showYearly && yearly ? (yearly / 12).toFixed(2) : null;
  return (
    <div
      className={`bg-white border-2 rounded-xl shadow-sm p-6 flex flex-col ${highlight ? "border-flag-red" : "border-slate-200"}`}
      data-testid={`billing-plan-${plan}`}
    >
      <div className="flex items-center gap-2">
        <h3 className="font-display text-2xl font-black text-flag-navy">{name}</h3>
        {highlight && <Sparkles className="h-4 w-4 text-flag-red" />}
        {current && (
          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded" data-testid={`billing-plan-current-${plan}`}>
            Current
          </span>
        )}
      </div>
      <div className="mt-2">
        {showYearly ? (
          <div>
            <div className="flex items-baseline gap-1" data-testid={`billing-plan-price-${plan}-yearly`}>
              <span className="text-4xl font-black text-flag-navy">${yearly.toFixed(2)}</span>
              <span className="text-sm text-slate-500 font-semibold">/year</span>
            </div>
            {monthlyEquiv && (
              <div className="text-xs text-slate-500 mt-1">
                ≈ ${monthlyEquiv}/mo · <span className="font-bold text-emerald-700">Save 10% billed annually</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-baseline gap-1" data-testid={`billing-plan-price-${plan}-monthly`}>
            <span className="text-4xl font-black text-flag-navy">${monthly.toFixed(2)}</span>
            <span className="text-sm text-slate-500 font-semibold">/month</span>
          </div>
        )}
      </div>
      <ul className="mt-4 space-y-2 text-sm text-slate-700 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onSubscribe}
        disabled={busy || disabled || current}
        data-testid={`billing-subscribe-${plan}`}
        className={`mt-5 h-11 font-bold ${highlight ? "bg-flag-red hover:bg-flag-red-hover" : "bg-flag-navy hover:bg-flag-navy-2"} text-white`}
      >
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
        {current ? "You're on this plan" : disabled ? "Not configured" : (<>{cta} <ArrowRight className="h-4 w-4 ml-1.5" /></>)}
      </Button>
    </div>
  );
}
